import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useAnimationControls } from 'motion/react';
import clsx from 'clsx';
import { newsletterSchema, type NewsletterFormData } from '@/types/forms';
import { trackConversion } from '@/utils/analytics';

interface NewsletterProps {
  className?: string;
  variant?: 'default' | 'compact' | 'footer';
}

export function Newsletter({ className, variant = 'default' }: NewsletterProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const controls = useAnimationControls();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/marketing/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to submit');

      // Track newsletter signup conversion
      trackConversion('newsletter_signup');

      setSubmitStatus('success');
      reset();
    } catch {
      setSubmitStatus('error');
      // Shake animation on error
      controls.start({
        x: [0, -8, 8, -8, 8, 0],
        transition: { duration: 0.4 },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={clsx(
          'text-center',
          variant === 'footer' ? 'py-4' : 'py-6',
          className
        )}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          className={clsx(
            'w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center',
            variant === 'footer' ? 'bg-accent/20' : 'bg-success-light'
          )}
        >
          <CheckCircleIcon className={clsx(
            'w-6 h-6',
            variant === 'footer' ? 'text-accent' : 'text-success'
          )} />
        </motion.div>
        <p className={clsx(
          'font-medium',
          variant === 'footer' ? 'text-paper' : 'text-success'
        )}>
          You're subscribed!
        </p>
        <p className={clsx(
          'text-sm mt-1',
          variant === 'footer' ? 'text-gray-400' : 'text-muted'
        )}>
          Check your inbox for a confirmation email.
        </p>
      </motion.div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={className}>
        <h4 className="font-heading font-semibold text-paper mb-2">
          Stay Updated
        </h4>
        <p className="text-gray-400 text-sm mb-4">
          Join 5,000+ service professionals getting weekly tips.
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <motion.div animate={controls} className="flex gap-2">
            <input
              type="email"
              {...register('email')}
              aria-label="Email address"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'footer-newsletter-error' : undefined}
              className={clsx(
                'flex-1 px-4 py-2.5 rounded-lg bg-white/10 text-paper placeholder-gray-400 border transition-colors focus:outline-none focus:ring-2 focus:ring-accent',
                errors.email ? 'border-red-500' : 'border-transparent'
              )}
              placeholder="Enter your email"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <LoadingSpinner className="w-5 h-5" />
              ) : (
                'Subscribe'
              )}
            </button>
          </motion.div>
          {errors.email && (
            <p id="footer-newsletter-error" className="text-red-400 text-sm mt-2" role="alert">
              {errors.email.message}
            </p>
          )}
          {submitStatus === 'error' && (
            <p className="text-red-400 text-sm mt-2" role="alert">
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={className}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <motion.div animate={controls} className="flex gap-2">
            <input
              type="email"
              {...register('email')}
              aria-label="Email address"
              aria-invalid={!!errors.email}
              className={clsx(
                'input flex-1',
                errors.email && 'input-error'
              )}
              placeholder="Enter your email"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '...' : 'Subscribe'}
            </button>
          </motion.div>
          {errors.email && (
            <p className="error-message mt-1" role="alert">
              {errors.email.message}
            </p>
          )}
        </form>
      </div>
    );
  }

  // Default variant - full newsletter section
  return (
    <div className={clsx('bg-cream rounded-2xl p-8 md:p-12', className)}>
      <div className="max-w-2xl mx-auto text-center">
        <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">
          Get expert tips in your inbox
        </h3>
        <p className="text-muted mb-6">
          Join 5,000+ service professionals receiving weekly insights on growing your business.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <motion.div
            animate={controls}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              {...register('email')}
              aria-label="Email address"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'newsletter-error' : undefined}
              className={clsx(
                'input flex-1',
                errors.email && 'input-error'
              )}
              placeholder="Enter your email"
            />
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className="btn-primary px-8 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </motion.button>
          </motion.div>
          {errors.email && (
            <p id="newsletter-error" className="error-message mt-2" role="alert">
              {errors.email.message}
            </p>
          )}
          {submitStatus === 'error' && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-600 text-sm mt-2"
              role="alert"
            >
              Something went wrong. Please try again.
            </motion.p>
          )}
        </form>

        <p className="text-xs text-muted mt-4">
          No spam, ever. Unsubscribe anytime.{' '}
          <a href="/privacy" className="link-accent">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

// Icons
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={clsx('animate-spin', className)} viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
